import { georgianDateFormat, georgianRelativeTime, type DateFormatter } from './georgian-dates';

const ZERO_DECIMAL = new Set(['JPY', 'KRW', 'CLP', 'VND', 'ISK']);

// A browser without Georgian locale data resolves ka-GE to en-US and shows 12-hour times; the app's own English format reads better.
const GEORGIAN_INTL = Intl.DateTimeFormat.supportedLocalesOf('ka-GE').length > 0;

export function intlLocale(locale: string): string {
  return locale === 'ka' && GEORGIAN_INTL ? 'ka-GE' : 'en-GB';
}

export function dateFormat(locale: string, options: Intl.DateTimeFormatOptions): DateFormatter {
  return locale === 'ka' && !GEORGIAN_INTL ? georgianDateFormat(options) : new Intl.DateTimeFormat(intlLocale(locale), options);
}

export function zonedDateFormat(locale: string, timeZone: string, options: Intl.DateTimeFormatOptions): DateFormatter {
  try {
    return dateFormat(locale, { ...options, timeZone });
  } catch {
    return dateFormat(locale, options);
  }
}

export function formatMoney(amountMinor: number, currency: string, locale = 'en'): string {
  const wholeUnits = ZERO_DECIMAL.has(currency);
  const major = amountMinor / (wholeUnits ? 1 : 100);
  try {
    return new Intl.NumberFormat(intlLocale(locale), { style: 'currency', currency }).format(major);
  } catch {
    return `${major.toFixed(wholeUnits ? 0 : 2)} ${currency}`;
  }
}

export function formatNumber(value: number, locale = 'en'): string {
  return new Intl.NumberFormat(intlLocale(locale)).format(value);
}

export function formatDate(iso: string | null | undefined, locale = 'en', options: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short', year: 'numeric' }): string {
  if (!iso) return '—';
  return dateFormat(locale, options).format(new Date(iso));
}

export function formatDateTime(iso: string | null | undefined, locale = 'en'): string {
  return formatDate(iso, locale, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

export function formatTime(iso: string, locale = 'en', timeZone?: string): string {
  const options: Intl.DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit' };
  return (timeZone ? zonedDateFormat(locale, timeZone, options) : dateFormat(locale, options)).format(new Date(iso));
}

export function weekdayLabel(iso: string, locale = 'en'): string {
  return dateFormat(locale, { weekday: 'short', day: 'numeric', month: 'short' }).format(new Date(iso));
}

export function relativeTime(iso: string, locale = 'en', now = Date.now()): string {
  const diff = Math.round((new Date(iso).getTime() - now) / 1000);
  const abs = Math.abs(diff);
  const rtf = new Intl.RelativeTimeFormat(intlLocale(locale), { numeric: 'auto' });
  const format = locale === 'ka' && !GEORGIAN_INTL ? georgianRelativeTime : (value: number, unit: 'second' | 'minute' | 'hour' | 'day') => rtf.format(value, unit);
  if (abs < 60) return format(diff, 'second');
  if (abs < 3600) return format(Math.round(diff / 60), 'minute');
  if (abs < 86_400) return format(Math.round(diff / 3600), 'hour');
  return format(Math.round(diff / 86_400), 'day');
}

export function toDateInput(iso: string | null | undefined): string {
  return iso ? iso.slice(0, 10) : '';
}

export function localYmd(date: Date, timeZone?: string): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(date);
}

const AWARD_MONTH: Intl.DateTimeFormatOptions = { month: 'long', year: 'numeric', timeZone: 'UTC' };
const AWARD_DAY: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' };

/** What an award is for: "October 2026" for a month, "9 October 2026" for a day. Both are calendar values, so no time zone moves them. */
export function formatAwardPeriod(period: { month: string | null; awardedOn: string | null }, locale = 'en'): string {
  if (period.month) return dateFormat(locale, AWARD_MONTH).format(new Date(`${period.month}-01T00:00:00Z`));
  if (period.awardedOn) return dateFormat(locale, AWARD_DAY).format(new Date(`${period.awardedOn}T00:00:00Z`));
  return '';
}

/** Whole calendar days from today to the day `iso` falls on, both in `timeZone` (the gym's, not the browser's). */
export function daysUntil(iso: string, timeZone?: string, now = new Date()): number {
  return Math.round((Date.parse(localYmd(new Date(iso), timeZone)) - Date.parse(localYmd(now, timeZone))) / 86_400_000);
}
